// =========================================================================
// Chart colour tokens — CSS custom-property references only. Fork-owned.
//
// Nothing here is a hex literal. Every value is a `var(--…)` string resolved
// by the browser against the active theme and the active brand, which is what
// makes one chart read correctly in light and dark, under Komatsu and under
// Meridian, without a re-render or a second palette.
//
// Three families, and they mean different things:
//
//   SERIES_*  — an ordinal ramp for "these categories are different", with no
//               ranking implied. Ledger categories, drive types, crews.
//   BAND_*    — the five-way machine-condition scale. Semantic and fixed:
//               "very poor" is the same red under every brand (styles/oem.css).
//   TONE_*    — the three-way status scale the demo calls good / warn / bad.
//
// Reaching for a SERIES colour to mean "bad" is the mistake this split
// exists to prevent.
// =========================================================================

/**
 * Ordinal series ramp, in assignment order.
 *
 * **The brand accent is deliberately not in here.** The demo's own `CATCOL`
 * (source line 1981) put `var(--accent)` second, which worked because the
 * demo had exactly one brand and its accent was crimson. This port ships two
 * brands, and Komatsu's accent is a deep blue — so on that lockup the accent
 * series and the `--info` series both render blue and two of five ledger
 * categories become indistinguishable. A categorical ramp has to stay
 * readable under every brand, so it is built from brand-independent tokens
 * only. The accent keeps its job of marking *the* thing that is interactive.
 *
 * Seven hues, each resolved per theme: blue, green, amber, red, plum, grey,
 * orange. That covers the widest series on the product (six mine sites) with
 * one spare.
 */
export const SERIES_COLORS: readonly string[] = [
  'var(--info)',
  'var(--band-good)',
  'var(--warn)',
  'var(--crit)',
  'var(--band-major)',
  'var(--ink-tertiary)',
  'var(--band-poor)',
];

/** Soft companion fills, index-matched to {@link SERIES_COLORS}. */
export const SERIES_SOFT: readonly string[] = [
  'var(--info-soft)',
  'var(--band-good-soft)',
  'var(--warn-soft)',
  'var(--crit-soft)',
  'var(--band-major-soft)',
  'var(--surface-3)',
  'var(--band-poor-soft)',
];

/** Colour for series `i`, wrapping so an over-long series never renders blank. */
export const seriesColor = (index: number): string =>
  SERIES_COLORS[((index % SERIES_COLORS.length) + SERIES_COLORS.length) % SERIES_COLORS.length];

/** Soft fill for series `i`, wrapping like {@link seriesColor}. */
export const seriesSoft = (index: number): string =>
  SERIES_SOFT[((index % SERIES_SOFT.length) + SERIES_SOFT.length) % SERIES_SOFT.length];

/**
 * The five ledger categories in the demo's own order, paired with their
 * colours. Exported as a pair so a legend and a stack cannot drift apart.
 *
 * Four of the five keep the demo's `CATCOL` token (source line 1981). Parts
 * moves off `var(--accent)` onto `var(--band-good)` for the reason given on
 * {@link SERIES_COLORS}: under the Komatsu lockup the accent is blue, and
 * Parts sits directly beside Labour in every stack on the cost page.
 */
export const COST_CATEGORIES = [
  'Labour',
  'Parts',
  'Freight',
  'External service',
  'Lost production',
] as const;

/** One of the five ledger categories. */
export type CostCategoryName = (typeof COST_CATEGORIES)[number];

/** Ledger category → its token. Index-matched to {@link COST_CATEGORIES}. */
export const COST_CATEGORY_COLOR: Readonly<Record<CostCategoryName, string>> = {
  Labour: 'var(--info)',
  Parts: 'var(--band-good)',
  Freight: 'var(--warn)',
  'External service': 'var(--ink-tertiary)',
  'Lost production': 'var(--crit)',
};

/** The five condition bands → their tokens (styles/oem.css). */
export const BAND_COLOR: Readonly<Record<string, string>> = {
  good: 'var(--band-good)',
  fair: 'var(--band-fair)',
  poor: 'var(--band-poor)',
  vpoor: 'var(--band-vpoor)',
  major: 'var(--band-major)',
};

/** The five condition bands → their soft fills. */
export const BAND_SOFT: Readonly<Record<string, string>> = {
  good: 'var(--band-good-soft)',
  fair: 'var(--band-fair-soft)',
  poor: 'var(--band-poor-soft)',
  vpoor: 'var(--band-vpoor-soft)',
  major: 'var(--band-major-soft)',
};

/** The demo's three-way KPI tone. `bad` is its name for critical. */
export type ChartTone = 'good' | 'warn' | 'bad' | 'flat';

/** Tone → its token. `flat` is the neutral ink, used when nothing is claimed. */
export const TONE_COLOR: Readonly<Record<ChartTone, string>> = {
  good: 'var(--good)',
  warn: 'var(--warn)',
  bad: 'var(--crit)',
  flat: 'var(--ink-tertiary)',
};

/** Tone → its soft fill. */
export const TONE_SOFT: Readonly<Record<ChartTone, string>> = {
  good: 'var(--good-soft)',
  warn: 'var(--warn-soft)',
  bad: 'var(--crit-soft)',
  flat: 'var(--surface-3)',
};

/** Resolve an engine tone string, falling back to neutral on anything unknown. */
export const toneColor = (tone: string): string =>
  TONE_COLOR[tone as ChartTone] ?? TONE_COLOR.flat;

/** Soft companion to {@link toneColor}. */
export const toneSoft = (tone: string): string => TONE_SOFT[tone as ChartTone] ?? TONE_SOFT.flat;

/** Axis rule / gridline colour. */
export const AXIS_COLOR = 'var(--hairline)';

/** Axis and label ink. */
export const AXIS_INK = 'var(--ink-tertiary)';

/** The track a gauge or a progress bar sits in. */
export const TRACK_COLOR = 'var(--surface-3)';
